<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { styleZoom } from "@/common/utils/style";
import { fileAvatarFileShow } from "@/common/api/rest/file/avatar";

const emit = defineEmits(["block:update"]);

const props = defineProps<{
  block: any;
  selectedBlock: string | null;
  zoom: number;
}>();

const images = ref<{ [key: string]: string }>({});

const columns = computed(() =>
  props.block.content?.columns?.filter((col: any) => !!col.visible),
);

const fields = computed(() =>
  props.block.content?.fields?.filter((f: any) => !!f.visible),
);

const trows = computed(() => {
  const rowsData = props.block.content?.rows || [];

  return rowsData.map((rowFields: any[]) => {
    // Para cada linha, percorremos as colunas visíveis
    return columns.value.map((col: any) => {
      // Encontramos o campo dentro da linha que pertence a esta coluna
      const items = rowFields
        .filter((f: any) => f.col === col.id && f.visible)
        .map((f: any) => {
          return {
            ...f,
            label: fields.value.find((field: any) => field.id === f.field)
              ?.label,
          };
        });

      return {
        columnId: col.id,
        fields: items || [], // Retorna o campo ou null se não houver dados para essa célula
      };
    });
  });
});

async function getImages() {
  const newImages = Object.keys(images.value).filter((id) => !images.value[id]);

  const promises = newImages.map((id) =>
    fileAvatarFileShow(props.block.account_id, id),
  );

  await Promise.all(promises).then((response) => {
    response.forEach((res, index) => {
      if (res.success) {
        const id = newImages[index];
        images.value[id] = res.data.base64;
      }
    });
  });
}

async function initialize() {
  const newImages = props.block.content.rows
    .flatMap((row: any[]) => row)
    .filter((item: any) => !images.value[item.value])
    .filter((item: any) => item.value && item.is_image)
    .reduce(
      (acc: any, item: any) => {
        acc[item.value] = "";
        return acc;
      },
      {} as { [key: string]: string },
    );
  images.value = { ...newImages, ...images.value };
  await getImages();
}

watch(
  () => props.block,
  async () => {
    if (!props.block) {
      return;
    }

    await initialize();
  },
  { immediate: true },
);

onMounted(async () => {
  if (!props.block) {
    return;
  }

  await initialize();
});
</script>

<template>
  <div
    class="table-block"
    :class="{
      selected: !!selectedBlock,
    }"
    :style="styleZoom(block.style, zoom)"
  >
    <template v-if="block.content?.title">
      <div class="table-title">
        {{ block.content?.title }}
      </div>
    </template>
    <table class="data-table">
      <tbody>
        <template v-for="(rowCells, rowIndex) in trows" :key="rowIndex">
          <tr>
            <template v-for="cell in rowCells" :key="cell.columnId">
              <td>
                <div class="col">
                  <template v-for="field in cell.fields" :key="field.id">
                    <div
                      class="item"
                      :class="{
                        is_imagem: field.is_image,
                      }"
                    >
                      <template v-if="field.is_image">
                        <img :src="images[field.value]" />
                      </template>
                      <template v-else>
                        <template v-if="field.label">
                          <div class="label">{{ field.label }}:</div>
                        </template>
                        <div class="value">
                          <template v-if="field.value">
                            {{ field.value }}
                          </template>
                          <template v-else> - </template>
                        </div>
                      </template>
                    </div>
                  </template>
                </div>
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-block {
  overflow: hidden;

  .table-title {
    padding-inline: 0.875em;
    padding-block: 0.3em;
    font-size: 1.1em;
    font-weight: bold;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
  }

  .data-table {
    width: 100%;
    min-height: 20px;
    border-collapse: separate;
    border-spacing: 0 1em;
    border: 0;
    table-layout: fixed;

    th,
    td {
      border: 1px solid #dee2e6;
      padding-inline: 0.875em;
      padding-block: 0.5em;
    }

    th {
      background: #f8f9fa;
      font-weight: bold;
      font-size: 0.875em;
      text-align: center;
    }

    td {
      background: #fff;
      font-weight: normal;
      font-size: 0.95em;
      text-align: center;
    }

    .col {
      vertical-align: top;
      white-space: normal;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
      text-align: left;

      &:not(:last-child) {
        border-right: 1px solid #dee2e6;
      }

      .item {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: start;
        justify-content: flex-start;
        row-gap: 0.5em;
        column-gap: 1rem;
        margin-block: 0.5em;

        &.is_imagem {
          grid-template-columns: 1fr;
          justify-items: center;

          img {
            width: 150px;
            height: auto;
          }
        }

        .label {
          min-width: 20%;
          font-weight: 400;
          word-break: break-word;
          overflow-wrap: break-word;
        }

        .value {
          font-weight: 700;
          word-break: break-word;
          overflow-wrap: break-word;
        }
      }
    }
  }
}
</style>
